import { TradeLeadCalculator } from "@/components/TradeLeadCalculator";
import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do painters get more jobs from Google in Colorado?",
    a: "The fastest path is a tuned Google Business Profile with weekly fresh project photos, plus dedicated landing pages for interior, exterior, cabinet refinishing, and commercial painting — instead of one generic 'painting services' page. We layer Google Ads in February–April to capture the planners who book the highest-paying exterior jobs early. Full guide: /answers/how-do-painters-get-more-jobs-from-google.",
  },
  {
    q: "What's the best marketing channel for a Colorado painting contractor?",
    a: "Local SEO + Google reviews is the highest-ROI channel because painting buyers read 10+ reviews before calling. Google Ads works extremely well for exterior season (May–September) and cabinet refinishing year-round. Social media (especially Instagram) helps for high-end residential work.",
  },
  {
    q: "How long does painting contractor SEO take to work?",
    a: "Most Colorado painting contractors see meaningful Map Pack movement in 60–120 days. Competitive terms like 'house painter Denver' usually take 4–6 months of consistent reviews, photos, and on-page work.",
  },
  {
    q: "Can you help with HOA-compliant exterior painting in Briargate or Castle Pines?",
    a: "Yes — and it's one of the easiest win categories most painters ignore. Dedicated HOA-color-approved landing pages for specific Colorado Springs and Castle Rock neighborhoods convert dramatically better than generic exterior pages.",
  },
  {
    q: "Should I build a separate page for cabinet refinishing?",
    a: "Yes — cabinet refinishing is the highest-margin job most painters bury in their service list. Building a dedicated cabinet-refinishing page that ranks separately captures buyers who'd otherwise go to specialty refinishing shops.",
  },
];

export const Route = createFileRoute("/painting-marketing-colorado")({
  head: () =>
    industryMarketingHead("Painting Contractor", "painting contractor", "painting-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Painting Contractor"
      industryLower="painting contractor"
      slug="painting-marketing-colorado"
      extraSections={<TradeLeadCalculator industry="Painting" defaultJob={4500} />}
      subheadline="We help Colorado painting contractors fill the exterior season and the off-season — interior, cabinets, and commercial."
      painPoints={[
        "Exterior season ends before the phone catches up",
        "Cabinet refinishing and high-margin commercial work are invisible on the site",
        "Reviews are decent but the website doesn't convert HOA-compliant buyers",
      ]}
      serviceDescriptions={{
        seo: "Rank for interior, exterior, cabinet refinishing, and commercial painting across every Colorado market you serve.",
        googleMaps: "Dominate the local Map Pack so 'house painter near me' searches come to your shop first.",
        webDesign: "Fast, mobile-first painter sites with weekly-updated project galleries and HOA color guides.",
        ppc: "Profitable Google Ads tuned to exterior season, cabinet refinishing, and commercial bids.",
        geoAi: "Get cited when Coloradans ask ChatGPT 'best painter near me' or 'how much does it cost to paint a house'.",
        socialMedia: "Before-and-after content (especially Instagram) that converts high-end residential buyers.",
      }}
      testimonial={{
        quote:
          "We finally have a website that shows what we actually do. Cabinet refinishing alone has covered the entire investment.",
        role: "Painting Contractor",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Painting marketing in Colorado Springs", description: "Help Springs painters own HOA-driven neighborhoods like Briargate, Northgate, and Stetson Hills." },
        { to: "/denver-digital-marketing", label: "Painting marketing in Denver", description: "Compete in Denver's residential and commercial painting market — neighborhood by neighborhood." },
        { to: "/castle-rock-digital-marketing", label: "Painting marketing in Castle Rock", description: "Premium HOA exterior, cabinet, and remodel painting across Castle Rock and Castle Pines." },
      ]}
    />
  ),
});
